import { formatDate } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  LOCALE_ID,
  output,
  Signal,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { CarouselDialogData } from '@app/components/dialogs/carousel-dialog/carousel-dialog.component';
import { FileActionsService } from '@app/shared/services/actions/file-actions.service';
import { FolderActionsService } from '@app/shared/services/actions/folder-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { FolderLoadState } from '@app/shared/services/folder-data.service';
import { SessionScopeService } from '@app/shared/services/session-scope.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import {
  ExplorerFolder,
  FileAttachmentCapabilities,
  FolderContext
} from '@app/shared/types/models/files/explorer-folder.model';
import { isValidExternalFolderUrl } from '@app/shared/types/models/files/external-folder-link.util';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { FileUploadInfo } from '@app/shared/types/models/files/file-upload-info';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import {
  PROFFEO_FILE_KIND_ICONS,
  ProffeoFileKind,
  proffeoFileKindFromMime
} from '@app/shared/ui/tokens/attachments.tokens';
import { ProffeoUiInputValue } from '@app/shared/ui/types/input.types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, Observable } from 'rxjs';
import { FileUploadListComponent } from '../file-upload-list/file-upload-list.component';
import { isFileViewable } from '../file.utils';
import {
  ariaSortFor,
  FileExplorerFolderRow,
  FileExplorerSort,
  FileExplorerSortState,
  formatFileSize,
  isHiddenStructuralFolder,
  sortFiles,
  toFolderRow
} from './file-explorer.utils';

interface FileExplorerBreadcrumb {
  id: string | null;
  name: string;
}

interface FileExplorerFileRow {
  file: FileInfo;
  fileKind: ProffeoFileKind;
  viewable: boolean;
  canSetAsAvatar: boolean;
  capabilities: FileAttachmentCapabilities;
}

/** Signals requested once, on an explicit expand, from the existing FolderActions cache. */
interface FileExplorerExpansion {
  folder: Signal<ExplorerFolder | undefined>;
  state: Signal<FolderLoadState>;
  children: Signal<ExplorerFolder[]>;
  files: Signal<FileInfo[] | undefined>;
}

interface FileExplorerRowBase {
  key: string;
  depth: number;
  indentRem: number;
  /** True for rows inside an inline-expanded folder; those rows are read-only. */
  nested: boolean;
  /**
   * Folder IDs below the displayed folder: up to and including the folder itself for folder rows,
   * or up to the owning folder for file rows (empty for files of the displayed folder).
   */
  path: string[];
}

interface FileExplorerFolderView extends FileExplorerRowBase {
  type: 'folder';
  row: FileExplorerFolderRow;
  name: string;
  dateLabel: string;
  sizeLabel: string;
  kindKey: string;
  icon: string;
  iconKind: 'folder';
  expandable: boolean;
  expanded: boolean;
  canRename: boolean;
  canDelete: boolean;
}

interface FileExplorerFileView extends FileExplorerRowBase {
  type: 'file';
  file: FileInfo;
  ownerFolderId: string;
  name: string;
  dateLabel: string;
  sizeLabel: string;
  kindKey: string;
  icon: string;
  iconKind: ProffeoFileKind;
  viewable: boolean;
  canSetAsAvatar: boolean;
  capabilities: FileAttachmentCapabilities;
}

interface FileExplorerStatusView extends FileExplorerRowBase {
  type: 'status';
  folderId: string;
  status: 'loading' | 'error';
}

type FileExplorerItemView = FileExplorerFolderView | FileExplorerFileView;
type FileExplorerRowView = FileExplorerItemView | FileExplorerStatusView;

interface FileExplorerRowBuild {
  rows: FileExplorerRowView[];
  sort: FileExplorerSortState;
  expanded: ReadonlySet<string>;
  expansions: ReadonlyMap<string, FileExplorerExpansion>;
  nestedAllowed: boolean;
  ancestors: Set<string>;
}

const INITIAL_LOAD_STATE: FolderLoadState = { loading: false, loaded: false };
const NO_CAPABILITIES: FileAttachmentCapabilities = { canRename: false, canMove: false, canDelete: false };
const EMPTY_VALUE = '—';
const INDENT_BASE_REM = 0.25;
const INDENT_STEP_REM = 1.25;
let nextExplorerId = 0;

/**
 * PROPOZYCJA — DO WERYFIKACJI: Finder-style file/folder browser built on the existing folder metadata
 * and file caches. Write controls require server capabilities; business attachment identity is retained.
 * Rows of inline-expanded folders are read-only so mutations always target the displayed folder.
 * Rationale: docs/file-explorer-finder-redesign.md.
 */
@Component({
  selector: 'proffeo-file-explorer',
  templateUrl: './file-explorer.component.html',
  styleUrl: './file-explorer.component.css',
  imports: [TranslatePipe, MatIconModule, UiButtonComponent, UiInputComponent, FileUploadListComponent]
})
export class FileExplorerComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly fileActions = inject(FileActionsService);
  private readonly folderActions = inject(FolderActionsService);
  private readonly fileDataService = inject(FileDataService);
  private readonly dialogService = inject(DialogService);
  private readonly translate = inject(TranslateService);
  private readonly activeCompanyId = inject(ActiveCompanyService).activeCompanyId;
  /** Signed-in user + company; unlike tokens() it survives an access-token refresh. */
  private readonly sessionKey = inject(SessionScopeService).key;

  private readonly rootId = signal<string | undefined>(undefined);
  private readonly breadcrumbIds = signal<string[]>([]);
  private readonly backStack = signal<string[][]>([]);
  private readonly rootFolderSignal = signal<Signal<ExplorerFolder | undefined>>(signal(undefined));
  private readonly rootStateSignal = signal<Signal<FolderLoadState>>(signal(INITIAL_LOAD_STATE));
  private readonly activeFolderSignal = signal<Signal<ExplorerFolder | undefined> | null>(null);
  private readonly activeStateSignal = signal<Signal<FolderLoadState> | null>(null);
  private readonly expandedIds = signal<ReadonlySet<string>>(new Set());
  private readonly expansions = signal<ReadonlyMap<string, FileExplorerExpansion>>(new Map());

  /**
   * Resets navigation, expansion and selection, then (re)establishes the root request on context/company/session
   * change. A token refresh is not a session change, so it must not throw the user back to the root.
   */
  private readonly resetOnContextChange = effect(() => {
    const context = this.context();
    this.activeCompanyId();
    this.sessionKey();
    untracked(() => {
      this.rootId.set(undefined);
      this.initializing.set(false);
      this.mutationError.set(false);
      this.mutationPending.set(false);
      this.breadcrumbIds.set([]);
      this.backStack.set([]);
      this.resetBrowseState();
      this.rootFolderSignal.set(this.folderActions.getRoot(context));
      this.rootStateSignal.set(this.folderActions.getRootState(context));
    });
  });

  private readonly syncRootId = effect(() => {
    const root = this.rootFolderSignal()();
    if (root) this.rootId.set(root.id);
  });

  /** Establishes the current-folder request signal whenever the navigation path changes. */
  private readonly syncActiveFolder = effect(() => {
    const ids = this.breadcrumbIds();
    untracked(() => {
      this.mutationPending.set(false);
      this.mutationError.set(false);
      if (ids.length === 0) {
        this.activeFolderSignal.set(null);
        this.activeStateSignal.set(null);
        return;
      }
      const folderId = ids[ids.length - 1];
      this.activeFolderSignal.set(this.folderActions.getById(folderId));
      this.activeStateSignal.set(this.folderActions.getStateById(folderId));
    });
  });

  private readonly children = computed<ExplorerFolder[]>(() => {
    const folder = this.currentFolder();
    return folder ? this.folderActions.getChildren(folder.id)() : [];
  });

  private readonly normalizedSearch = computed<string>(() =>
    String(this.searchQuery() ?? '')
      .trim()
      .toLowerCase()
  );

  private readonly files = computed<FileInfo[]>(() => {
    const folder = this.currentFolder();
    return folder ? (this.folderActions.getFiles(folder.id)() ?? []) : [];
  });

  private readonly sortedFiles = computed<FileInfo[]>(() => {
    const query = this.normalizedSearch();
    const matching = query
      ? this.files().filter(file => (file.name ?? '').toLowerCase().includes(query))
      : this.files();
    return sortFiles(matching, this.sort());
  });

  private readonly searchedChildren = computed<FileExplorerFolderRow[]>(() => {
    const query = this.normalizedSearch();
    const rows = this.children()
      .filter(folder => !isHiddenStructuralFolder(folder) && (!this.selectionMode() || folder.type === 24))
      .map(folder => toFolderRow(folder));
    const matching = query ? rows.filter(row => this.folderDisplayName(row).toLowerCase().includes(query)) : rows;
    return this.sortFolderRows(matching, this.sort());
  });

  private readonly itemRows = computed<FileExplorerItemView[]>(() =>
    this.rows().filter((row): row is FileExplorerItemView => row.type !== 'status')
  );

  private readonly viewableFiles = computed<FileInfo[]>(() => this.sortedFiles().filter(file => isFileViewable(file)));

  private readonly fileRows = computed<FileExplorerFileRow[]>(() =>
    (this.selectionMode() ? [] : this.sortedFiles()).map(file => ({
      file,
      fileKind: proffeoFileKindFromMime(file.type ?? ''),
      viewable: isFileViewable(file),
      capabilities: this.currentFolder()?.fileCapabilities[file.id] ?? NO_CAPABILITIES,
      canSetAsAvatar:
        this.canSetAsAvatar() &&
        this.context().kind === 'task' &&
        file.isImage() &&
        this.avatarFileId() !== file.id &&
        [2, 24].includes(this.currentFolder()?.type)
    }))
  );

  protected readonly canEditExternalLink = computed(() => {
    const folder = this.currentFolder();
    return !this.selectionMode() && !!folder?.canManageExternalFolder && [0, 1, 2].includes(folder.type);
  });

  protected readonly externalLink = computed(() => {
    const url = this.currentFolder()?.externalFolderUrl;
    if (!url || !isValidExternalFolderUrl(url)) return undefined;
    return { url: url.trim(), host: new URL(url.trim()).hostname };
  });

  protected readonly titleKey = computed(() =>
    this.selectionMode() ? 'files.explorer.select-destination' : 'files.explorer.title'
  );
  protected readonly emptyKey = computed(() =>
    this.selectionMode() ? 'files.explorer.no-subfolders' : 'files.explorer.empty'
  );

  protected readonly activeUploads = computed<FileUploadInfo[]>(() => {
    const folderId = this.breadcrumbIds().at(-1) ?? this.rootId();
    return folderId ? this.fileDataService.getActiveUploads(folderId, FileContext.folderFile)() : [];
  });

  protected readonly needsInitialization = computed(() => {
    const rootState = this.rootStateSignal()();
    if (!rootState.loaded || rootState.loading || rootState.error) return false;
    const root = this.rootFolderSignal()();
    if (!root) return true;
    const children = this.folderActions.getChildren(root.id)();
    const keys = new Set(children.map(folder => folder.systemKey));
    if (this.context().kind !== 'task') return [1, 2, 3, 4, 5, 6].some(key => !keys.has(key));
    const types = new Set(children.map(folder => folder.type));
    return [4, 5, 6].some(key => !keys.has(key)) || [17, 14, 15].some(type => !types.has(type));
  });

  /** Unique per instance: the move dialog can render a second explorer over a task's Files tab. */
  protected readonly titleId = `proffeo-file-explorer-title-${nextExplorerId++}`;
  protected readonly initializing = signal(false);
  protected readonly mutationPending = signal(false);
  protected readonly mutationError = signal(false);
  protected readonly dropActive = signal(false);
  protected readonly selectedKey = signal<string | null>(null);

  protected readonly searchQuery = signal<ProffeoUiInputValue>('');
  protected readonly sort = signal<FileExplorerSortState>({ by: 'name', reversed: false });

  protected readonly ariaSort = computed(() => {
    const sort = this.sort();
    return {
      name: ariaSortFor('name', sort),
      date: ariaSortFor('date', sort),
      size: ariaSortFor('size', sort)
    };
  });

  protected readonly currentFolder = computed<ExplorerFolder | undefined>(() => {
    const active = this.activeFolderSignal();
    return active ? active() : this.rootFolderSignal()();
  });

  protected readonly currentState = computed<FolderLoadState>(() => {
    const active = this.activeStateSignal();
    return active ? active() : this.rootStateSignal()();
  });

  protected readonly viewState = computed<'not-initialized' | 'loading' | 'error' | 'loaded'>(() => {
    const state = this.currentState();
    if (state.error) {
      return 'error';
    }
    if (state.loading) {
      return 'loading';
    }
    if (state.loaded) {
      return this.currentFolder() ? 'loaded' : 'not-initialized';
    }
    return 'not-initialized';
  });

  protected readonly breadcrumbTrail = computed<FileExplorerBreadcrumb[]>(() => {
    const trail: FileExplorerBreadcrumb[] = [{ id: null, name: '' }];
    for (const folderId of this.breadcrumbIds()) {
      const folder = this.folderActions.getById(folderId)();
      trail.push({ id: folderId, name: folder ? this.folderDisplayName(toFolderRow(folder)) : '' });
    }
    return trail;
  });

  protected readonly canGoBack = computed(() => this.backStack().length > 0);
  protected readonly canGoUp = computed(() => this.breadcrumbIds().length > 0);

  /** Displayed folder rows, with inline-expanded descendants inserted depth-first below their folder. */
  protected readonly rows = computed<FileExplorerRowView[]>(() => {
    const folder = this.currentFolder();
    if (!folder) return [];
    const build: FileExplorerRowBuild = {
      rows: [],
      sort: this.sort(),
      expanded: this.expandedIds(),
      expansions: this.expansions(),
      nestedAllowed: !this.selectionMode() && !this.normalizedSearch(),
      ancestors: new Set([folder.id])
    };
    for (const row of this.searchedChildren()) {
      this.appendFolderRows(build, row, 0, [row.folder.id]);
    }
    for (const row of this.fileRows()) {
      build.rows.push(this.toFileView(row, 0, [], folder.id, false));
    }
    return build.rows;
  });

  /** Roving tabindex target: the selected row, otherwise the first item. */
  protected readonly focusKey = computed<string | null>(() => {
    const selected = this.selectedKey();
    const items = this.itemRows();
    return items.some(row => row.key === selected) ? selected : (items[0]?.key ?? null);
  });

  public readonly context = input.required<FolderContext>();
  public readonly selectionMode = input(false);
  public readonly folderSelected = output<string>();
  public readonly canSetAsAvatar = input(false);
  public readonly avatarFileId = input<string | undefined>();
  public readonly avatarSet = output<FileInfo>();

  /** Localized label when the folder is a recognized system/semantic category, otherwise the custom name. */
  private folderDisplayName(row: FileExplorerFolderRow): string {
    return row.labelKey ? String(this.translate.instant(row.labelKey)) : (row.folder.name ?? '');
  }

  private sortFolderRows(rows: FileExplorerFolderRow[], sort: FileExplorerSortState): FileExplorerFolderRow[] {
    const direction = sort.by === 'name' && sort.reversed ? -1 : 1;
    return rows
      .map(row => ({ row, displayName: this.folderDisplayName(row) }))
      .sort((first, second) => direction * first.displayName.localeCompare(second.displayName))
      .map(entry => entry.row);
  }

  private appendFolderRows(
    build: FileExplorerRowBuild,
    row: FileExplorerFolderRow,
    depth: number,
    path: string[]
  ): void {
    const id = row.folder.id;
    const expandable = build.nestedAllowed && !build.ancestors.has(id);
    const expanded = expandable && build.expanded.has(id);
    build.rows.push(this.toFolderView(row, depth, path, expandable, expanded));
    const source = expanded ? build.expansions.get(id) : undefined;
    if (!source) return;

    const state = source.state();
    const loaded = source.folder();
    if (state.error || (!loaded && (state.loading || !state.loaded))) {
      build.rows.push({
        type: 'status',
        key: `status:${path.join('/')}`,
        depth: depth + 1,
        indentRem: this.indentFor(depth + 1),
        nested: true,
        path,
        folderId: id,
        status: state.error ? 'error' : 'loading'
      });
      return;
    }
    if (!loaded) return;

    build.ancestors.add(id);
    const childRows = source
      .children()
      .filter(child => !isHiddenStructuralFolder(child))
      .map(child => toFolderRow(child));
    for (const child of this.sortFolderRows(childRows, build.sort)) {
      this.appendFolderRows(build, child, depth + 1, [...path, child.folder.id]);
    }
    for (const file of sortFiles(source.files() ?? [], build.sort)) {
      const fileRow: FileExplorerFileRow = {
        file,
        fileKind: proffeoFileKindFromMime(file.type ?? ''),
        viewable: isFileViewable(file),
        capabilities: NO_CAPABILITIES,
        canSetAsAvatar: false
      };
      build.rows.push(this.toFileView(fileRow, depth + 1, path, loaded.id, true));
    }
    build.ancestors.delete(id);
  }

  private toFolderView(
    row: FileExplorerFolderRow,
    depth: number,
    path: string[],
    expandable: boolean,
    expanded: boolean
  ): FileExplorerFolderView {
    const nested = depth > 0;
    const editable = !nested && !this.selectionMode() && !row.folder.isSystem;
    return {
      type: 'folder',
      key: `folder:${path.join('/')}`,
      depth,
      indentRem: this.indentFor(depth),
      nested,
      path,
      row,
      name: this.folderDisplayName(row),
      dateLabel: this.dateLabel(row.folder.createdAt),
      sizeLabel: EMPTY_VALUE,
      kindKey: 'files.explorer.kind.folder',
      icon: 'folder',
      iconKind: 'folder',
      expandable,
      expanded,
      canRename: editable && row.folder.canRename,
      canDelete: editable && row.folder.canDelete
    };
  }

  private toFileView(
    row: FileExplorerFileRow,
    depth: number,
    path: string[],
    ownerFolderId: string,
    nested: boolean
  ): FileExplorerFileView {
    return {
      type: 'file',
      key: `file:${ownerFolderId}:${row.file.id}`,
      depth,
      indentRem: this.indentFor(depth),
      nested,
      path,
      file: row.file,
      ownerFolderId,
      name: row.file.name ?? '',
      dateLabel: this.dateLabel(row.file.createdAt),
      sizeLabel: formatFileSize(row.file.size, this.locale) ?? EMPTY_VALUE,
      kindKey: `files.explorer.kind.${row.fileKind}`,
      icon: PROFFEO_FILE_KIND_ICONS[row.fileKind],
      iconKind: row.fileKind,
      viewable: row.viewable,
      canSetAsAvatar: row.canSetAsAvatar,
      capabilities: row.capabilities
    };
  }

  private indentFor(depth: number): number {
    return INDENT_BASE_REM + depth * INDENT_STEP_REM;
  }

  private dateLabel(value: Date | string | undefined): string {
    if (!value) return EMPTY_VALUE;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? EMPTY_VALUE : formatDate(date, 'dd.MM.yyyy', this.locale);
  }

  private resetBrowseState(): void {
    this.expandedIds.set(new Set());
    this.expansions.set(new Map());
    this.selectedKey.set(null);
    this.dropActive.set(false);
  }

  /** Single navigation entry point: records history and clears inline expansion/selection. */
  private navigate(ids: string[], selectKey: string | null = null): void {
    const current = this.breadcrumbIds();
    if (ids.length === current.length && ids.every((id, index) => id === current[index])) return;
    this.backStack.update(stack => [...stack, current]);
    this.breadcrumbIds.set(ids);
    this.resetBrowseState();
    this.selectedKey.set(selectKey);
  }

  private focusRow(key: string): void {
    const element = Array.from(this.host.nativeElement.querySelectorAll<HTMLTableRowElement>('tr[data-row-key]')).find(
      row => row.dataset['rowKey'] === key
    );
    element?.focus();
  }

  private moveSelection(row: FileExplorerItemView, offset: number | 'first' | 'last'): void {
    const items = this.itemRows();
    if (items.length === 0) return;
    const index = items.findIndex(item => item.key === row.key);
    let nextIndex = items.length - 1;
    if (offset === 'first') nextIndex = 0;
    else if (offset !== 'last') nextIndex = Math.min(items.length - 1, Math.max(0, index + offset));
    const next = items[nextIndex];
    this.selectedKey.set(next.key);
    this.focusRow(next.key);
  }

  private isFromControl(event: Event | undefined): boolean {
    const target = event?.target as HTMLElement | null;
    return !!target?.closest?.('button, a, input');
  }

  private nestedViewableFiles(folderId: string): Signal<FileInfo[]> {
    const source = this.expansions().get(folderId);
    return computed(() => sortFiles(source?.files() ?? [], this.sort()).filter(file => isFileViewable(file)));
  }

  protected navigateTo(folderId: string | null): void {
    if (folderId === null) {
      this.navigate([]);
      return;
    }
    const ids = this.breadcrumbIds();
    const index = ids.indexOf(folderId);
    if (index >= 0) {
      this.navigate(ids.slice(0, index + 1));
    }
  }

  protected goBack(): void {
    const stack = this.backStack();
    const previous = stack.at(-1);
    if (!previous) return;
    this.backStack.set(stack.slice(0, -1));
    this.breadcrumbIds.set(previous);
    this.resetBrowseState();
  }

  protected goUp(): void {
    const ids = this.breadcrumbIds();
    if (ids.length > 0) this.navigate(ids.slice(0, -1), `folder:${ids.at(-1)}`);
  }

  protected selectRow(row: FileExplorerItemView, event?: MouseEvent): void {
    // Touch devices have no practical double-click: a tap opens, like a link.
    if ((event as PointerEvent | undefined)?.pointerType === 'touch' && !this.isFromControl(event)) {
      this.openRow(row);
      return;
    }
    this.selectedKey.set(row.key);
  }

  protected openRowFromPointer(row: FileExplorerItemView, event: MouseEvent): void {
    if (!this.isFromControl(event)) this.openRow(row);
  }

  /** Folder → navigate in; viewable file → preview; other file → download. */
  protected openRow(row: FileExplorerItemView): void {
    if (row.type === 'folder') {
      this.navigate([...this.breadcrumbIds(), ...row.path]);
      return;
    }
    if (row.viewable) {
      this.previewRow(row);
      return;
    }
    this.downloadRow(row);
  }

  protected handleRowKeydown(event: KeyboardEvent, row: FileExplorerItemView): void {
    if (event.target !== event.currentTarget) return;
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.openRow(row);
        return;
      case ' ':
        event.preventDefault();
        this.selectedKey.set(row.key);
        return;
      case 'ArrowDown':
        event.preventDefault();
        this.moveSelection(row, 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.moveSelection(row, -1);
        return;
      case 'Home':
        event.preventDefault();
        this.moveSelection(row, 'first');
        return;
      case 'End':
        event.preventDefault();
        this.moveSelection(row, 'last');
        return;
      case 'ArrowRight':
        if (row.type === 'folder' && row.expandable && !row.expanded) {
          event.preventDefault();
          this.toggleFolder(row);
        }
        return;
      case 'ArrowLeft':
        event.preventDefault();
        if (row.type === 'folder' && row.expanded) {
          this.toggleFolder(row);
        } else if (row.nested) {
          const parentPath = row.type === 'folder' ? row.path.slice(0, -1) : row.path;
          const parentKey = `folder:${parentPath.join('/')}`;
          this.selectedKey.set(parentKey);
          this.focusRow(parentKey);
        }
        return;
    }
  }

  /** Expands lazily: the folder's signals are requested only here, from the shared FolderActions cache. */
  protected toggleFolder(row: FileExplorerFolderView): void {
    if (!row.expandable) return;
    const id = row.row.folder.id;
    if (row.expanded) {
      this.expandedIds.update(ids => {
        const next = new Set(ids);
        next.delete(id);
        return next;
      });
      return;
    }
    if (!this.expansions().has(id)) {
      const folder = this.folderActions.getById(id);
      const expansion: FileExplorerExpansion = {
        folder,
        state: this.folderActions.getStateById(id),
        children: this.folderActions.getChildren(id),
        files: this.folderActions.getFiles(id)
      };
      this.expansions.update(map => new Map(map).set(id, expansion));
    }
    this.expandedIds.update(ids => new Set(ids).add(id));
  }

  protected retryExpansion(folderId: string): void {
    this.folderActions.reload(folderId);
  }

  /** Opens the folder that owns a nested file and selects the file there, where its actions are available. */
  protected showInFolder(row: FileExplorerFileView): void {
    if (!row.nested) return;
    this.navigate([...this.breadcrumbIds(), ...row.path], `file:${row.ownerFolderId}:${row.file.id}`);
  }

  protected sortByColumn(column: FileExplorerSort): void {
    this.sort.update(sort => ({ by: column, reversed: sort.by === column && !sort.reversed }));
  }

  protected acceptDrag(event: DragEvent): void {
    if (this.selectionMode() || !this.currentFolder()?.canUploadFiles || this.mutationPending()) return;
    if (!Array.from(event.dataTransfer?.types ?? []).includes('Files')) return;
    event.preventDefault();
    this.dropActive.set(true);
  }

  protected openUploadPicker(input: HTMLInputElement): void {
    if (this.selectionMode() || !this.currentFolder()?.canUploadFiles || this.mutationPending()) return;
    input.click();
  }

  protected uploadSelectedFiles(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    this.onFilesAdded(Array.from(input.files ?? []));
    input.value = '';
  }

  protected leaveDrag(event: DragEvent): void {
    const related = event.relatedTarget as Node | null;
    if (!related || !(event.currentTarget as HTMLElement).contains(related)) this.dropActive.set(false);
  }

  protected dropFiles(event: DragEvent): void {
    this.dropActive.set(false);
    // Do not process a drop already handled by another drop target.
    if (event.defaultPrevented) return;
    const files = event.dataTransfer?.files;
    if (!files?.length) return;
    event.preventDefault();
    this.onFilesAdded(Array.from(files));
  }

  protected renameFile(fileId: string): void {
    const folder = this.currentFolder();
    const file = this.fileRows().find(row => row.file.id === fileId)?.file;
    if (this.selectionMode() || !folder?.fileCapabilities[fileId]?.canRename || !file || this.mutationPending()) return;
    const stillCurrent = this.captureFolderScope(folder.id);
    const name = file.name ?? '';
    const dot = name.lastIndexOf('.');
    const extension = dot >= 0 && dot < name.length - 1 ? name.slice(dot) : '';
    this.dialogService
      .openFileExplorerNameDialog({ title: 'files.explorer.rename', name, extension })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(chosen => {
        if (chosen && chosen !== name && stillCurrent())
          this.runMutation(() => this.folderActions.renameAttachment(folder.id, fileId, chosen), stillCurrent);
      });
  }

  protected moveFile(fileId: string): void {
    const folder = this.currentFolder();
    if (this.selectionMode() || !folder?.fileCapabilities[fileId]?.canMove || this.mutationPending()) return;
    const stillCurrent = this.captureFolderScope(folder.id);
    this.dialogService
      .openFileExplorerMoveDialog({ context: this.context() })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(target => {
        if (target && target !== folder.id && stillCurrent())
          this.runMutation(() => this.folderActions.moveAttachment(folder.id, fileId, target), stillCurrent);
      });
  }

  protected deleteFile(fileId: string): void {
    const folder = this.currentFolder();
    const file = this.fileRows().find(row => row.file.id === fileId)?.file;
    if (this.selectionMode() || !folder?.fileCapabilities[fileId]?.canDelete || !file || this.mutationPending()) return;
    const stillCurrent = this.captureFolderScope(folder.id);
    const context = this.context();
    this.dialogService
      .openConfirmationDialog({
        title: 'files.explorer.delete-file',
        desc: this.translate.instant('files.explorer.delete-file-description', { name: file.name })
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed && stillCurrent())
          this.runMutation(
            () =>
              this.folderActions.deleteAttachment(folder.id, fileId, context.kind === 'task' ? context.id : undefined),
            stillCurrent
          );
      });
  }

  protected selectFolder(): void {
    const folder = this.currentFolder();
    if (this.selectionMode() && folder?.canUploadFiles && [0, 1, 2, 24].includes(folder.type))
      this.folderSelected.emit(folder.id);
  }

  protected onFilesAdded(files: File[]): void {
    if (this.selectionMode()) return;
    const folder = this.currentFolder();
    if (!folder?.canUploadFiles || this.mutationPending()) return;
    const context = this.context();
    files.forEach((file, orderNo) =>
      this.folderActions.upload(
        folder.id,
        { file, orderNo, description: undefined, fileTags: [] },
        context.kind === 'task' ? context.id : undefined
      )
    );
  }

  protected cancelUpload(uploadId: string | undefined): void {
    if (uploadId) this.fileActions.cancelUpload(uploadId);
  }

  protected dismissUpload(uploadId: string | undefined): void {
    if (uploadId) this.fileActions.dismissUpload(uploadId);
  }

  protected editExternalLink(): void {
    const folder = this.currentFolder();
    if (!this.canEditExternalLink() || !folder || this.mutationPending()) return;
    const stillCurrent = this.captureFolderScope(folder.id);
    this.dialogService
      .openFileExplorerExternalLinkDialog({ url: folder.externalFolderUrl })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result && result.url !== (folder.externalFolderUrl ?? null) && stillCurrent())
          this.runMutation(() => this.folderActions.setExternalLink(folder.id, result.url), stillCurrent);
      });
  }

  protected createFolder(): void {
    if (this.selectionMode()) return;
    const folder = this.currentFolder();
    if (!folder?.canCreateFolder || this.mutationPending()) return;
    const stillCurrent = this.captureFolderScope(folder.id);
    this.dialogService
      .openFileExplorerNameDialog({ title: 'files.explorer.create-folder' })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(name => {
        if (name && stillCurrent()) this.runMutation(() => this.folderActions.create(folder.id, name), stillCurrent);
      });
  }

  protected renameFolder(folder: ExplorerFolder): void {
    if (this.selectionMode()) return;
    const parent = this.currentFolder();
    if (!parent || folder.isSystem || !folder.canRename || this.mutationPending()) return;
    if (!parent.subFolderIds.includes(folder.id) && !this.children().some(child => child.id === folder.id)) return;
    const stillCurrent = this.captureFolderScope(parent.id);
    this.dialogService
      .openFileExplorerNameDialog({ title: 'files.explorer.rename', name: folder.name })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(name => {
        if (name && name !== folder.name && stillCurrent())
          this.runMutation(() => this.folderActions.rename(folder.id, name), stillCurrent);
      });
  }

  protected deleteFolder(folder: ExplorerFolder): void {
    if (this.selectionMode()) return;
    const parent = this.currentFolder();
    if (!parent || folder.isSystem || !folder.canDelete || this.mutationPending()) return;
    if (!parent.subFolderIds.includes(folder.id) && !this.children().some(child => child.id === folder.id)) return;
    const stillCurrent = this.captureFolderScope(parent.id);
    this.dialogService
      .openConfirmationDialog({
        title: 'files.explorer.delete-folder',
        desc: 'files.explorer.delete-folder-description'
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed && stillCurrent())
          this.runMutation(() => this.folderActions.delete(folder.id, parent.id), stillCurrent);
      });
  }

  private captureFolderScope(folderId: string): () => boolean {
    if (this.currentFolder()?.id !== folderId) return () => false;
    const navigation = this.breadcrumbIds();
    const context = this.context();
    const company = this.activeCompanyId();
    const session = this.sessionKey();
    return () =>
      context.kind === this.context().kind &&
      context.id === this.context().id &&
      company === this.activeCompanyId() &&
      session === this.sessionKey() &&
      this.breadcrumbIds() === navigation;
  }

  private runMutation(source: () => Observable<unknown>, stillCurrent: () => boolean): void {
    if (!stillCurrent() || this.mutationPending()) return;
    this.mutationPending.set(true);
    this.mutationError.set(false);
    source()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (stillCurrent()) this.mutationPending.set(false);
        })
      )
      .subscribe({
        error: () => {
          if (stillCurrent()) this.mutationError.set(true);
        }
      });
  }

  protected initialize(): void {
    if (this.selectionMode()) return;
    if (this.initializing() || !this.needsInitialization()) return;
    const context = this.context();
    const company = this.activeCompanyId();
    const session = this.sessionKey();
    const stillCurrent = (): boolean =>
      context.kind === this.context().kind &&
      context.id === this.context().id &&
      company === this.activeCompanyId() &&
      session === this.sessionKey();
    this.initializing.set(true);
    this.mutationError.set(false);
    this.folderActions
      .initialize(context)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (stillCurrent()) this.initializing.set(false);
        })
      )
      .subscribe({
        error: () => {
          if (stillCurrent()) this.mutationError.set(true);
        }
      });
  }

  protected retry(): void {
    // Snapshot before reload: loading state can synchronously remove the displayed rows.
    // Only visible expanded branches belong to this refresh; collapsed/search-hidden cache stays lazy.
    const expandedFolderIds = new Set(
      this.rows().flatMap(row => (row.type === 'folder' && row.expanded ? [row.row.folder.id] : []))
    );
    const folderId = this.breadcrumbIds().at(-1);
    if (folderId) this.folderActions.reload(folderId);
    else this.folderActions.reloadRoot(this.context());
    expandedFolderIds.delete(folderId ?? this.rootId());
    expandedFolderIds.forEach(id => this.folderActions.reload(id));
  }

  protected onSetAsAvatar(fileId: string): void {
    const row = this.fileRows().find(row => row.file.id === fileId);
    if (row?.canSetAsAvatar) this.avatarSet.emit(row.file);
  }

  /** Previews within the owning folder: the displayed folder, or the inline-expanded folder for nested rows. */
  protected previewRow(row: FileExplorerFileView): void {
    if (!row.viewable) return;
    const files = row.nested ? this.nestedViewableFiles(row.ownerFolderId) : this.viewableFiles;
    const data: CarouselDialogData = {
      files,
      currentIndex: files().findIndex(file => file.id === row.file.id)
    };
    this.dialogService.openCarouselDialog(data);
  }

  protected downloadRow(row: FileExplorerFileView): void {
    this.fileDataService.download(row.file);
  }
}
